import moment from "moment";
import { useState } from "react";
import { Button } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../../../components/loaderMessage/loaderMessage";
import CallingRuleTriggersSection from "../../../../../components/modals/AddNewCallingRuleModal/CallingRuleTriggersSection";
import DeleteStepConfirmModal from "../../../../../components/modals/DeleteStepConfirmModal/deleteStepConfirmModal";
import PharmacyOrderUpdateRuleCard from "../../../../../components/pharmacyOrderUpdates/PharmacyOrderUpdateRuleCard";
import { useFirstStpConfig } from "./useFirstStpConfig";
import { useFirstStpReviewRules } from "./useFirstStpReviewRules";

const FirstStpLogic = () => {
  const { data: firstStpDoc, loading: configLoading } = useFirstStpConfig();
  const {
    rules,
    triggers,
    conditionConfig,
    configKey,
    actionFieldOptions,
    actionValueConfig,
    isDirty,
    saving,
    ruleLoadedVersion,
    handleAddNewRuleClick,
    handleRuleLabelChange,
    handleRuleSubtextChange,
    handleRuleActiveToggle,
    handleRuleConditionChange,
    handleRuleActionFieldChange,
    handleRuleActionTypeChange,
    handleRuleActionStringValueChange,
    handleRuleActionMultiValueChange,
    handleRuleAddAction,
    handleRuleRemoveAction,
    handleDeleteRule,
    handleTriggerTypeChange,
    handleTriggerScheduleChange,
    handleAddTrigger,
    handleRemoveTrigger,
    handleSave,
    handleDiscard,
    lastSavedMeta,
  } = useFirstStpReviewRules(firstStpDoc);

  const [pendingDeleteRuleId, setPendingDeleteRuleId] = useState<string | null>(
    null,
  );

  const formattedLastUpdated =
    lastSavedMeta?.updated_at != null
      ? moment(lastSavedMeta.updated_at).format("DD MMM YYYY HH:mm")
      : null;

  if (configLoading) {
    return (
      <div className="first-stp-logic__loading flex h-full min-h-0 flex-col">
        <LoaderMessage message="Loading first STP review rules..." />
      </div>
    );
  }

  return (
    <div className="first-stp-logic-layout flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="first-stp-logic-layout__content mb-1 mt-2 flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col gap-2 pb-4">
          {rules.map((rule) => (
            <PharmacyOrderUpdateRuleCard
              key={rule.id}
              ruleId={rule.id}
              label={rule.label}
              subtext={rule.subtext}
              active={rule.active}
              condition={rule.condition}
              actions={rule.actions}
              conditionConfig={conditionConfig}
              actionValueConfig={actionValueConfig}
              actionFieldOptions={actionFieldOptions}
              configKey={configKey}
              ruleLoadedVersion={ruleLoadedVersion}
              defaultSubtext="First STP review rule"
              onLabelChange={(value) => handleRuleLabelChange(rule.id, value)}
              onSubtextChange={(value) =>
                handleRuleSubtextChange(rule.id, value)
              }
              onActiveToggle={() => handleRuleActiveToggle(rule.id)}
              onConditionChange={(value) =>
                handleRuleConditionChange(rule.id, value)
              }
              onActionFieldChange={(index, value) =>
                handleRuleActionFieldChange(rule.id, index, value)
              }
              onActionTypeChange={(index, value) =>
                handleRuleActionTypeChange(rule.id, index, value)
              }
              onActionStringValueChange={(index, value) =>
                handleRuleActionStringValueChange(rule.id, index, value)
              }
              onActionMultiValueChange={(index, values) =>
                handleRuleActionMultiValueChange(rule.id, index, values)
              }
              onAddAction={() => handleRuleAddAction(rule.id)}
              onRemoveAction={(index) => handleRuleRemoveAction(rule.id, index)}
              onDelete={() => setPendingDeleteRuleId(rule.id)}
            />
          ))}

          <div
            role="button"
            tabIndex={0}
            className="flex cursor-pointer flex-row items-center justify-center gap-2 rounded-md border border-primaryGray-14 p-4 transition-colors hover:border-primaryGray-12 hover:bg-primaryGray-16"
            onClick={handleAddNewRuleClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleAddNewRuleClick();
              }
            }}
          >
            <div>+</div>
            <div className="text-sm">Add New Rule</div>
          </div>

          <CallingRuleTriggersSection
            triggers={triggers}
            loadedVersion={ruleLoadedVersion}
            onAddTrigger={handleAddTrigger}
            onRemoveTrigger={handleRemoveTrigger}
            onTypeChange={handleTriggerTypeChange}
            onScheduleChange={handleTriggerScheduleChange}
          />
        </div>
      </div>

      <div className="first-stp-logic-layout__footer flex flex-shrink-0 flex-row items-center justify-between gap-4 border-t border-primaryGray-16 pt-2">
        <div className="flex flex-row gap-4 text-tiny">
          {lastSavedMeta != null &&
          (lastSavedMeta.updated_by != null ||
            lastSavedMeta.updated_at != null) ? (
            <div className="mt-1">
              Last updated:{" "}
              {formattedLastUpdated != null && (
                <span className="font-semibold">{formattedLastUpdated}</span>
              )}
              {lastSavedMeta.updated_by != null && (
                <>
                  {" "}
                  by{" "}
                  <span className="font-semibold">
                    {lastSavedMeta.updated_by}
                  </span>
                </>
              )}
            </div>
          ) : (
            <div className="mt-1 text-primaryGray-12">Not saved yet</div>
          )}
        </div>
        <div className="flex flex-row gap-2">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={handleDiscard}
            disabled={!isDirty}
            children="Cancel"
          />
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            disabled={!isDirty || saving}
            children={saving ? "Saving..." : "Save"}
          />
        </div>
      </div>

      {pendingDeleteRuleId != null && (
        <DeleteStepConfirmModal
          onConfirm={() => {
            handleDeleteRule(pendingDeleteRuleId);
            setPendingDeleteRuleId(null);
          }}
          onClose={() => setPendingDeleteRuleId(null)}
        />
      )}
    </div>
  );
};

export default FirstStpLogic;
